import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, money, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  Building2, Users2, Wrench, CheckCircle2, PauseCircle, HardDrive,
  LifeBuoy, CalendarClock, LogOut, Plus, ArrowRight, ShieldCheck
} from "lucide-react";

function AdminHeader() {
  const { user, logout } = useAuth(); const nav = useNavigate();
  return (
    <header className="h-16 bg-[#0F1725] text-white flex items-center px-6 gap-3 sticky top-0 z-10">
      <img src="/brand/dispatch-logo.png" alt="Amden Dispatch" className="h-8 w-8 rounded-md" />
      <div><div className="font-head font-bold leading-none">Amden Dispatch</div><div className="text-[11px] text-slate-400">Platform control centre</div></div>
      <nav className="ml-8 flex gap-1 text-sm">
        <button onClick={() => nav("/admin")} className="px-3 py-1.5 rounded hover:bg-white/10 transition-colors">Overview</button>
        <button onClick={() => nav("/admin/companies")} className="px-3 py-1.5 rounded hover:bg-white/10 transition-colors">Companies</button>
      </nav>
      <div className="ml-auto flex items-center gap-3 text-sm"><span className="text-slate-300">{user?.name}</span>
        <button data-testid="admin-logout" onClick={() => { logout(); nav("/login"); }} className="text-slate-400 hover:text-red-400 transition-colors"><LogOut className="h-5 w-5" /></button></div>
    </header>
  );
}

function Stat({ icon: Icon, label, value, tone = "#0284C7" }) {
  return (
    <div className="kpi-card bg-white border border-slate-200 rounded-md p-4">
      <div className="flex items-center justify-between"><span className="text-xs text-slate-500">{label}</span><Icon className="h-4 w-4" style={{ color: tone }} /></div>
      <div className="mt-2 text-2xl font-head font-bold text-slate-900">{value}</div>
    </div>
  );
}

export default function AdminDashboard() {
  const [d, setD] = useState(null);
  const nav = useNavigate();
  useEffect(() => { api.get("/admin/stats").then((r) => setD(r.data)); }, []);
  if (!d) return <div className="min-h-screen bg-slate-50"><AdminHeader /><div className="p-6 text-slate-400">Loading…</div></div>;

  const openWorkspace = (cid) => { localStorage.setItem("active_company_id", cid); nav("/app/dashboard"); };

  return (
    <div className="min-h-screen bg-slate-50">
      <AdminHeader />
      <div className="p-6 space-y-6">
        <div><h1 className="text-2xl font-head font-bold text-slate-900">Platform Overview</h1><p className="text-sm text-slate-500">All Amden client companies at a glance</p></div>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
          <Stat icon={Building2} label="Total Companies" value={d.total_companies} />
          <Stat icon={CheckCircle2} label="Active" value={d.active_companies} tone="#16A34A" />
          <Stat icon={PauseCircle} label="Suspended" value={d.suspended_companies} tone="#D97706" />
          <Stat icon={Users2} label="Active Users" value={d.total_users} tone="#9333EA" />
          <Stat icon={Wrench} label="Jobs (Month)" value={d.jobs_created_month} />
          <Stat icon={CheckCircle2} label="Completed (Month)" value={d.jobs_completed_month} tone="#16A34A" />
          <Stat icon={HardDrive} label="Storage" value={`${d.storage_mb} MB`} tone="#64748B" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 bg-white border border-slate-200 rounded-md p-4">
            <div className="flex items-center justify-between mb-3"><h3 className="font-head font-semibold">Client Companies</h3>
              <Button size="sm" className="btn-accent" data-testid="admin-view-companies" onClick={() => nav("/admin/companies")}>Manage <ArrowRight className="h-4 w-4 ml-1" /></Button></div>
            <table className="w-full text-sm">
              <thead className="bg-slate-50"><tr>{["Company", "Plan", "Users", "Jobs", "Modules", "Status", ""].map((h) => <th key={h} className="text-left font-medium text-slate-500 px-3 py-2">{h}</th>)}</tr></thead>
              <tbody>
                {d.companies.map((c) => (
                  <tr key={c.id} className="border-t border-slate-100">
                    <td className="px-3 py-2.5 font-medium text-slate-800 flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: c.accent_color }} />{c.name}</td>
                    <td className="px-3 py-2.5 text-slate-600">{c.plan}</td><td className="px-3 py-2.5">{c.users}</td>
                    <td className="px-3 py-2.5">{c.jobs}</td><td className="px-3 py-2.5">{c.modules}</td>
                    <td className="px-3 py-2.5 capitalize text-slate-600">{c.status}</td>
                    <td className="px-3 py-2.5"><Button size="sm" variant="outline" data-testid={`admin-open-${c.id}`} onClick={() => openWorkspace(c.id)}>Open workspace</Button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="space-y-4">
            <div className="bg-white border border-slate-200 rounded-md p-4"><h3 className="font-head font-semibold text-sm mb-2 flex items-center gap-1"><LifeBuoy className="h-4 w-4" />Open Support Issues</h3><div className="text-3xl font-head font-bold">{d.open_issues}</div></div>
            <div className="bg-white border border-slate-200 rounded-md p-4"><h3 className="font-head font-semibold text-sm mb-2 flex items-center gap-1"><CalendarClock className="h-4 w-4" />Upcoming Renewals</h3>
              {d.renewals.map((r, i) => <div key={i} className="flex justify-between text-sm py-1 border-b border-slate-100 last:border-0"><span className="text-slate-700">{r.company}</span><span className="text-slate-500">{fmtDate(r.renewal_date)}</span></div>)}
            </div>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-md p-4">
          <h3 className="font-head font-semibold text-sm mb-2 flex items-center gap-1"><ShieldCheck className="h-4 w-4" />Recent Platform Activity</h3>
          <div className="space-y-1 max-h-64 overflow-y-auto scroll-thin">
            {d.activity.map((a) => <div key={a.id} className="text-xs text-slate-500 py-1 border-b border-slate-100 last:border-0"><span className="text-slate-700 font-medium">{a.user_name || "System"}</span> {a.action} {a.entity} {a.record_ref || ""} · {new Date(a.timestamp).toLocaleString()}</div>)}
          </div>
        </div>
      </div>
    </div>
  );
}

export { AdminHeader };
